/**
 * The orcaloops member surface's keyboard shortcuts (the seam of the UI redesign).
 *
 * One table, read in two places: {@link shortcutFor} turns a keydown into a
 * named shortcut for {@link OrcaloopsFrame} to dispatch, and
 * {@link SHORTCUT_LABELS} is what a button prints beside its action so a person
 * learns the key by using the app. A shortcut a screen does not handle is
 * simply ignored by that screen.
 *
 * Single-key shortcuts never fire while a person is typing into an input,
 * textarea, select or contenteditable; the command shortcuts (⌘K, escape)
 * always do. Pure — no DOM, no React.
 *
 * @since 1.0.0
 */
import { Match } from "effect";

/** @since 1.0.0 */
export type OrcaloopsShortcut =
  | "jump"
  | "escape"
  | "run"
  | "answer"
  | "layers"
  | "fit"
  | "next"
  | "prev"
  | "open"
  | "view-minimal"
  | "view-author"
  | "view-operator"
  | "stop";

/** The key a button prints beside its action. @since 1.0.0 */
export const SHORTCUT_LABELS: Readonly<Record<OrcaloopsShortcut, string>> = {
  jump: "⌘K",
  escape: "esc",
  run: "R",
  answer: "A",
  layers: "L",
  fit: "F",
  next: "J",
  prev: "K",
  open: "↵",
  "view-minimal": "1",
  "view-author": "2",
  "view-operator": "3",
  stop: "S",
};

/** The slice of a keyboard event the table reads. @since 1.0.0 */
export interface ShortcutEventLike {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  /** The event target's tag name, upper-cased, or `null` when there is none. */
  readonly targetTag: string | null;
  /** Whether the event target is contenteditable. */
  readonly targetEditable: boolean;
}

/** The tags a single-key shortcut must never steal a keystroke from. */
const TYPING_TAGS: ReadonlySet<string> = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** Whether the event target is something a person types into. @since 1.0.0 */
export const isTypingTarget = (event: Pick<ShortcutEventLike, "targetTag" | "targetEditable">): boolean =>
  event.targetEditable || (event.targetTag !== null && TYPING_TAGS.has(event.targetTag));

/** The minimal target shape {@link shortcutEventOf} reads. @since 1.0.0 */
export interface ShortcutTargetLike {
  readonly tagName?: string;
  readonly isContentEditable?: boolean;
}

/** The minimal event shape {@link shortcutEventOf} reads — a React or DOM keyboard event fits. @since 1.0.0 */
export interface ShortcutSourceEvent {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly target: ShortcutTargetLike | null;
}

/** Project a keyboard event onto {@link ShortcutEventLike}. @since 1.0.0 */
export const shortcutEventOf = (event: ShortcutSourceEvent): ShortcutEventLike => ({
  key: event.key,
  metaKey: event.metaKey,
  ctrlKey: event.ctrlKey,
  altKey: event.altKey,
  targetTag: event.target === null || event.target.tagName === undefined ? null : event.target.tagName.toUpperCase(),
  targetEditable: event.target !== null && event.target.isContentEditable === true,
});

/**
 * The shortcut a keydown names, or `null` when it names none.
 *
 * @since 1.0.0
 */
export const shortcutFor = (event: ShortcutEventLike): OrcaloopsShortcut | null => {
  const command = event.metaKey || event.ctrlKey;
  if (command && event.key.toLowerCase() === "k") return "jump";
  if (event.key === "Escape") return "escape";
  if (command || event.altKey || isTypingTarget(event)) return null;
  return Match.value(event.key).pipe(
    Match.when("r", (): OrcaloopsShortcut => "run"),
    Match.when("a", (): OrcaloopsShortcut => "answer"),
    Match.when("l", (): OrcaloopsShortcut => "layers"),
    Match.when("f", (): OrcaloopsShortcut => "fit"),
    Match.when("j", (): OrcaloopsShortcut => "next"),
    Match.when("k", (): OrcaloopsShortcut => "prev"),
    Match.when("Enter", (): OrcaloopsShortcut => "open"),
    Match.when("1", (): OrcaloopsShortcut => "view-minimal"),
    Match.when("2", (): OrcaloopsShortcut => "view-author"),
    Match.when("3", (): OrcaloopsShortcut => "view-operator"),
    Match.when("s", (): OrcaloopsShortcut => "stop"),
    Match.orElse((): OrcaloopsShortcut | null => null),
  );
};
